import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/FirebaseAuthContext";
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationContainer from './components/notifications/NotificationContainer';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import LoadingSpinner from "./components/socialmessaging/LoadingSpinner";
import { useAuth } from "./contexts/FirebaseAuthContext";
import { useVerificationCheck } from "./hooks/useVerificationCheck";
import VerificationGuard from "./components/auth/VerificationGuard";
import NotificationPermissionPrompt from './components/notifications/NotificationPermissionPrompt';

// Lazy Load Pages
const Settings = React.lazy(() => import('./pages/Settings'));
const Help = React.lazy(() => import('./pages/Help'));
const GettingStarted = React.lazy(() => import('./pages/GettingStarted'));
const FindingRooms = React.lazy(() => import('./pages/FindingRooms'));
const RentingVisits = React.lazy(() => import('./pages/RentingVisits'));
const CommunityMessaging = React.lazy(() => import('./pages/CommunityMessaging'));
const AccountPrivacy = React.lazy(() => import('./pages/AccountPrivacy'));
const TroubleshootingSafety = React.lazy(() => import('./pages/TroubleshootingSafety'));
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const SuggestionsPage = React.lazy(() => import("./pages/SuggestionsPage"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));

// NEW SOCIAL MESSAGING IMPORTS
const MessageLayout = React.lazy(() => import("./components/socialmessaging/MessageLayout"));
const MessageSettings = React.lazy(() => import("./pages/MessageSettings"));

// Pages / Components
const SplashScreen = React.lazy(() => import("./components/SplashScreen"));
const Homepage = React.lazy(() => import("./pages/Homepage"));
const Login = React.lazy(() => import("./pages/Login"));
const SignupPage = React.lazy(() => import("./components/Signup/SignupPage"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const AdvancedSearchListings = React.lazy(() => import("./pages/AdvancedSearchListings"));
const SearchPage = React.lazy(() => import("./pages/SearchPage"));
const IndividualRoomListing = React.lazy(() => import("./pages/IndividualRoomListing"));
const RoommateMatchingPlatform = React.lazy(() => import("./pages/RoommateMatchingPlatform"));
const RoommatesPage = React.lazy(() => import("./pages/RoommatesPage"));
const CommunityHub = React.lazy(() => import("./pages/CommunityHub"));
const HashtagPage = React.lazy(() => import("./pages/HashtagPage"));
const PostDetailPage = React.lazy(() => import('./pages/PostDetailPage'));
const JobDetailPage = React.lazy(() => import("./pages/JobDetailPage"));
const AdDetailPage = React.lazy(() => import("./pages/AdDetailPage"));
const ProductDetailPage = React.lazy(() => import("./pages/ProductDetailPage"));
const EventDetailPage = React.lazy(() => import("./pages/EventDetailPage"));
const SearchListings = React.lazy(() => import("./pages/SearchListings"));
const RoomListing = React.lazy(() => import("./pages/RoomListing"));
const PostRoomPage = React.lazy(() => import("./pages/PostRoomPage"));
const EditRoomPage = React.lazy(() => import("./pages/EditRoomPage"));
const YourListings = React.lazy(() => import("./pages/YourListings"));
const SavedListings = React.lazy(() => import("./pages/SavedListings"));
const AdminPropertyManagerPage = React.lazy(() => import("./pages/AdminPropertyManagerPage"));
const FollowersPage = React.lazy(() => import("./pages/FollowersPage"));
const FollowingPage = React.lazy(() => import("./pages/FollowingPage"));
const ShareLandingPage = React.lazy(() => import("./pages/ShareLandingPage"));
const ShareInterface = React.lazy(() => import("./pages/ShareInterface"));
const ShareDetailPage = React.lazy(() => import("./pages/ShareDetailPage"));
const PrivacyPolicy = React.lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = React.lazy(() => import("./pages/legal/CookiePolicy"));
const EmailVerificationPage = React.lazy(() => import("./pages/EmailVerificationPage"));

// Protected Route Component (Firebase Auth)
// Protected Route Component (Firebase Auth)
// Protected Route Component (Firebase Auth)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { needsVerification } = useVerificationCheck();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }
  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (needsVerification) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }
  return user ? <Navigate to="/dashboardpage" /> : children;
};

export default function App() {

  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <div className="App">
            <ScrollToTop />
            <Suspense fallback={
              <div className="flex justify-center items-center min-h-screen">
                <LoadingSpinner />
              </div>
            }>
              <Routes>
                {/* UPDATED SOCIAL MESSAGING ROUTES - FIXED CONSISTENCY */}
                {/* Main Messages Inbox with Tabs */}
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute>
                      <MessageLayout />
                    </ProtectedRoute>
                  }
                />


                {/* Individual Conversation Chat */}
                <Route
                  path="/messages/chat/:conversationId"
                  element={
                    <ProtectedRoute>
                      <MessageLayout />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/messages/:conversationId"
                  element={
                    <ProtectedRoute>
                      <MessageLayout />
                    </ProtectedRoute>
                  }
                />

                {/* Message Settings */}
                <Route
                  path="/messages/settings"
                  element={
                    <ProtectedRoute>
                      <MessageSettings />
                    </ProtectedRoute>
                  }
                />

                {/* LEGACY ROUTES - REDIRECT TO MAIN MESSAGES */}
                <Route
                  path="/pricing/messages"
                  element={<Navigate to="/messages?tab=listings" replace />}
                />

                {/* Notifications Page Route */}
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />


                {/* ALL YOUR EXISTING ROUTES REMAIN EXACTLY THE SAME */}
                <Route path="/your-listings" element={<VerificationGuard><YourListings /></VerificationGuard>} />
                <Route path="/saved-listings" element={<VerificationGuard><SavedListings /></VerificationGuard>} />

                {/* Admin Property Manager Approval Page */}
                <Route
                  path="/admin/property-managers"
                  element={
                    <ProtectedRoute>
                      <AdminPropertyManagerPage />
                    </ProtectedRoute>
                  }
                />
                {/* Homepage - always accessible (remove from PublicRoute) */}
                <Route path="/home" element={<VerificationGuard><Homepage /></VerificationGuard>} />
                {/* Redirect root "/" to /splash */}
                <Route path="/" element={<Navigate to="/splash" />} />
                {/* Splash screen */}
                <Route path="/splash" element={<SplashScreen />} />
                {/* Public routes - redirect to dashboard if already authenticated */}
                <Route
                  path="/splash"
                  element={
                    <PublicRoute>
                      <SplashScreen />
                    </PublicRoute>
                  }
                />
                {/* Auth routes - redirect to dashboard if already authenticated */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <PublicRoute>
                      <SignupPage />
                    </PublicRoute>
                  }
                />

                {/* Email Verification Route */}
                <Route
                  path="/verify-email"
                  element={<EmailVerificationPage />}
                />

                <Route path="/settings" element={<VerificationGuard><Settings /></VerificationGuard>} />
                <Route path="/help" element={<VerificationGuard><Help /></VerificationGuard>} />
                <Route path="/getting-started" element={<VerificationGuard><GettingStarted /></VerificationGuard>} />
                <Route path="/finding-rooms" element={<VerificationGuard><FindingRooms /></VerificationGuard>} />
                <Route path="/renting-visits" element={<VerificationGuard><RentingVisits /></VerificationGuard>} />
                <Route path="/community-messaging" element={<VerificationGuard><CommunityMessaging /></VerificationGuard>} />
                <Route path="/account-privacy" element={<VerificationGuard><AccountPrivacy /></VerificationGuard>} />
                <Route path="/troubleshooting-safety" element={<VerificationGuard><TroubleshootingSafety /></VerificationGuard>} />

                <Route
                  path="/forgot-password"
                  element={
                    <PublicRoute>
                      <ForgotPassword />
                    </PublicRoute>
                  }
                />
                {/* Protected dashboard route */}
                <Route
                  path="/dashboardpage"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/post-room"
                  element={
                    <ProtectedRoute>
                      <PostRoomPage />
                    </ProtectedRoute>
                  }
                />

                {/* Listings / Search */}
                <Route path="/advanced_search_listings" element={<VerificationGuard><AdvancedSearchListings /></VerificationGuard>} />
                <Route path="/search" element={<VerificationGuard><SearchPage /></VerificationGuard>} />
                <Route path="/room/:id" element={<VerificationGuard><IndividualRoomListing /></VerificationGuard>} />
                <Route path="/roommate_matching_platform" element={<VerificationGuard><RoommateMatchingPlatform /></VerificationGuard>} />
                <Route path="/listings" element={<VerificationGuard><SearchListings /></VerificationGuard>} />
                <Route path="/listings/:id" element={<VerificationGuard><RoomListing /></VerificationGuard>} />
                {/* Edit Room Route - FIXED */}
                <Route path="/edit-room/:id" element={<ProtectedRoute><EditRoomPage /></ProtectedRoute>} />
                {/* Roommates / Community */}
                <Route path="/roommates" element={<VerificationGuard><RoommatesPage /></VerificationGuard>} />
                <Route path="/community" element={<VerificationGuard><CommunityHub /></VerificationGuard>} />
                <Route path="/community/hashtag/:hashtag" element={<VerificationGuard><HashtagPage /></VerificationGuard>} />
                <Route path="/post/:postId" element={<VerificationGuard><PostDetailPage /></VerificationGuard>} />

                {/* ✅ ADDED: Four Detail Page Routes - UPDATED WITH SOCIAL MESSAGING */}
                <Route path="/ad/:adId" element={<VerificationGuard><AdDetailPage /></VerificationGuard>} />
                <Route path="/business/:id" element={<VerificationGuard><AdDetailPage /></VerificationGuard>} />
                <Route path="/job/:jobId" element={<VerificationGuard><JobDetailPage /></VerificationGuard>} />
                <Route path="/product/:productId" element={<VerificationGuard><ProductDetailPage /></VerificationGuard>} />
                <Route path="/event/:eventId" element={<VerificationGuard><EventDetailPage /></VerificationGuard>} />

                {/* User Profile Route - CORRECTED PATH */}
                <Route path="/profile/:userId" element={<VerificationGuard><UserProfile /></VerificationGuard>} />
                {/* User Profile Route for /user/:userId (for modal navigation) */}
                <Route path="/user/:userId" element={<VerificationGuard><UserProfile /></VerificationGuard>} />

                {/* Followers and Following Pages */}
                <Route path="/profile/:userId/followers" element={<VerificationGuard><FollowersPage /></VerificationGuard>} />
                <Route path="/profile/:userId/following" element={<VerificationGuard><FollowingPage /></VerificationGuard>} />
                {/* Following Page - For Start Messaging Button */}
                <Route
                  path="/following"
                  element={
                    <ProtectedRoute>
                      <FollowingPage />
                    </ProtectedRoute>
                  }
                />

                {/* Suggestions Page - NEW */}
                <Route
                  path="/suggestions"
                  element={
                    <ProtectedRoute>
                      <SuggestionsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Surplus Sharing Feature */}
                <Route path="/share" element={<VerificationGuard><ShareLandingPage /></VerificationGuard>} />
                <Route path="/share/interface"
                  element={
                    <ProtectedRoute>
                      <ShareInterface />
                    </ProtectedRoute>
                  }
                />
                <Route path="/share/:itemId" element={<VerificationGuard><ShareDetailPage /></VerificationGuard>} />

                {/* Legal Routes */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
              </Routes>
            </Suspense>
            <NotificationContainer />
            <NotificationPermissionPrompt />
            <CookieConsentBanner />
          </div>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
